import React from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  EnvelopeIcon,
  LinkIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import Modal from '../ui/Modal';
import UserAvatar from '../ui/UserAvatar';
import { useLanguage } from '../../contexts/LanguageContext';

function formatMemberSince(value, locale) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' });
}

function VendorPickerCard({ vendor, selected, onSelect, labels, locale }) {
  const memberSince = formatMemberSince(vendor.created_at, locale);

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(String(vendor.id))}
      className={`group relative flex h-full w-full flex-col overflow-hidden rounded-2xl border p-4 text-left transition-[border-color,box-shadow,background-color] duration-200 ease-out sm:p-5 ${
        selected
          ? 'border-orange-400/80 bg-gradient-to-br from-orange-50/90 via-white to-amber-50/50 shadow-glow ring-2 ring-orange-400/25     '
          : 'border-secondary-200/80 bg-white/90 hover:border-orange-200/70 hover:shadow-soft   '
      }`}
    >
      {selected ? (
        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
          <CheckCircleIcon className="h-3.5 w-3.5" />
          {labels.vendorFilterSelected}
        </span>
      ) : null}

      <div className="flex items-start gap-4">
        <div className="shrink-0 overflow-hidden rounded-2xl border-2 border-white shadow-soft ring-1 ring-secondary-200/60  ">
          <UserAvatar
            src={vendor.avatar_url}
            alt={vendor.username}
            size="md"
            variant="brand"
          />
        </div>

        <div className="min-w-0 flex-1 pr-8">
          <p className="truncate text-base font-bold text-secondary-900 ">
            {vendor.username}
          </p>
          <p className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-secondary-500 ">
            <EnvelopeIcon className="h-3.5 w-3.5 shrink-0 text-secondary-400" />
            <span className="truncate">{vendor.email}</span>
          </p>
          {memberSince ? (
            <p className="mt-1 text-xs text-secondary-400 ">
              {labels.vendorFilterMemberSince?.replace('{date}', memberSince) || memberSince}
            </p>
          ) : null}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <div className="rounded-xl bg-secondary-50/90 px-2 py-2 text-center ">
          <UsersIcon className="mx-auto h-4 w-4 text-orange-500" />
          <p className="mt-1 text-sm font-bold tabular-nums text-secondary-900 ">
            {vendor.clients_count ?? 0}
          </p>
          <p className="text-[10px] font-medium uppercase tracking-wide text-secondary-500 ">
            {labels.vendorFilterClients}
          </p>
        </div>
        <div className="rounded-xl bg-secondary-50/90 px-2 py-2 text-center ">
          <LinkIcon className="mx-auto h-4 w-4 text-amber-500" />
          <p className="mt-1 text-sm font-bold tabular-nums text-secondary-900 ">
            {vendor.referrals_count ?? 0}
          </p>
          <p className="text-[10px] font-medium uppercase tracking-wide text-secondary-500 ">
            {labels.vendorFilterReferrals}
          </p>
        </div>
        <div className="rounded-xl bg-secondary-50/90 px-2 py-2 text-center ">
          <RocketLaunchIcon className="mx-auto h-4 w-4 text-violet-500" />
          <p className="mt-1 text-sm font-bold tabular-nums text-secondary-900 ">
            {vendor.prospects_count ?? 0}
          </p>
          <p className="text-[10px] font-medium uppercase tracking-wide text-secondary-500 ">
            {labels.vendorFilterProspects}
          </p>
        </div>
      </div>
    </motion.button>
  );
}

function VendorFilterModal({
  isOpen,
  onClose,
  vendors = [],
  selectedVendorId = '',
  onSelect,
  labels = {},
}) {
  const { locale } = useLanguage();

  function handleSelect(vendorId) {
    onSelect?.(vendorId);
    onClose?.();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={labels.vendorFilterModalTitle}
      size="xl"
    >
      <div className="space-y-4">
        <motion.button
          type="button"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ y: -1 }}
          whileTap={{ scale: 0.99 }}
          onClick={() => handleSelect('')}
          className={`flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-[border-color,box-shadow,background-color] duration-200 ease-out sm:p-5 ${
            !selectedVendorId
              ? 'border-orange-400/80 bg-gradient-to-r from-orange-50/90 via-white to-amber-50/40 shadow-glow ring-2 ring-orange-400/25     '
              : 'border-secondary-200/80 bg-white/90 hover:border-orange-200/70 hover:shadow-soft   '
          }`}
        >
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 shadow-soft">
            <UserGroupIcon className="h-7 w-7 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-secondary-900 ">
              {labels.vendorFilterAllTitle}
            </p>
            <p className="mt-0.5 text-sm text-secondary-500 ">
              {labels.vendorFilterAllDesc}
            </p>
          </div>
          {!selectedVendorId ? (
            <CheckCircleIcon className="h-6 w-6 shrink-0 text-orange-500" />
          ) : null}
        </motion.button>

        {vendors.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {vendors.map((vendor, index) => (
              <motion.div
                key={vendor.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.04, 0.24) }}
                className="h-full"
              >
                <VendorPickerCard
                  vendor={vendor}
                  selected={String(selectedVendorId) === String(vendor.id)}
                  onSelect={handleSelect}
                  labels={labels}
                  locale={locale}
                />
              </motion.div>
            ))}
          </div>
        ) : (
          <p className="rounded-2xl border border-dashed border-secondary-300/80 px-4 py-10 text-center text-sm text-secondary-500  ">
            {labels.vendorFilterNoVendors}
          </p>
        )}
      </div>
    </Modal>
  );
}

export default VendorFilterModal;
